import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import CryptoJS from "crypto-js";
import logo from "../assets/BizVantage.png";
import bg from "../assets/background-2.png";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import SignUpPlanStep from "../components/SignUpPlanStep";
import FullScreenLoader from "../components/FullScreenLoader";
import { DiscountProvider } from "../contexts/DiscountContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const SIGNIN_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/signIn";
const RESET_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/forgotPass";
const REGISTER_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/register";

const CREATE_SESSION_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/create-subscription-session";

const LoginNewV1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [activeTab, setActiveTab] = useState("signin");
  const [signUpStep, setSignUpStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState("Core");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [forgotDialog, setForgotDialog] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [subscriptionCancelledDialog, setSubscriptionCancelledDialog] =
    useState(false);
  const [subscriptionSuccessDialog, setSubscriptionSuccessDialog] =
    useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);

  const AES_SECRET = useMemo(
    () =>
      (typeof import.meta !== "undefined" &&
        import.meta.env?.VITE_JWT_SECRET) ||
      process.env.REACT_APP_JWT_SECRET ||
      "",
    [],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cancelled = params.get("subscriptionCancelled");
    const success = params.get("subscriptionSuccess");

    if (cancelled === "true" || cancelled === "1") {
      setSubscriptionCancelledDialog(true);
    }

    if (success === "true" || success === "1") {
      setSubscriptionSuccessDialog(true);
    }
  }, [location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");

    if (!email.trim() || !password.trim()) {
      setErr("Email and password are required.");
      alert("Email and password are required.");
      return;
    }
    if (!AES_SECRET) {
      const msg =
        "Missing AES secret. Configure VITE_JWT_SECRET or REACT_APP_JWT_SECRET.";
      setErr(msg);
      alert(msg);
      return;
    }

    try {
      setLoading(true);
      const encryptedPassword = CryptoJS.AES.encrypt(
        password,
        AES_SECRET,
      ).toString();

      const { data } = await axios.post(
        SIGNIN_URL,
        { email: email.trim(), password: encryptedPassword },
        { headers: { "Content-Type": "application/json" } },
      );

      if (data?.authToken) {
        localStorage.setItem("bv.registerResponse", JSON.stringify(data));
        navigate("/dashboard");
      } else {
        const msg =
          data?.message ||
          "Login failed. Please check your credentials and try again.";
        setErr(msg);
        alert(msg);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Login failed. Please check your credentials.";
      setErr(msg);
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpStep1 = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }
    setSignUpStep(2);
  };

  const handleSignUpComplete = async () => {
    setErr("");
    try {
      setLoading(true);

      const clearBody = {
        fullName: name.trim(),
        phone: title.trim(),
        planOption: selectedPlan,
        email: email.trim(),
        password: CryptoJS.AES.encrypt(password.trim(), AES_SECRET).toString(),
      };

      // For paid plans, create checkout session first and complete registration after payment.
      if (selectedPlan === "Core" || selectedPlan === "Pro") {
        try {
          // Save pending signup so CheckoutResult can complete registration after success
          localStorage.setItem("bv.pendingSignup", JSON.stringify(clearBody));

          const payload = {
            fullName: name.trim(),
            phone: title.trim(),
            planOption: selectedPlan,
            email: email.trim(),
            currency: selectedCurrency,
            password: CryptoJS.AES.encrypt(
              password.trim(),
              AES_SECRET,
            ).toString(),
          };

          const sessionResp = await axios.post(CREATE_SESSION_URL, payload, {
            headers: { "Content-Type": "application/json" },
          });

          const sessionData = sessionResp?.data || {};

          if (sessionData?.url) {
            setCheckoutLoading(true);
            window.location.href = sessionData.url;
            return;
          }

          alert("Unable to start checkout. Please try again later.");
          localStorage.removeItem("bv.pendingSignup");
          return;
        } catch (err) {
          console.error("create session error", err?.response || err);
          alert(
            err?.response?.data?.message ||
              "Failed to create checkout session.",
          );
          localStorage.removeItem("bv.pendingSignup");
          navigate("/dashboard");
          return;
        }
      }

      // free plan or no payment required -> register immediately
      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(clearBody),
      });
      const data = await res.json().catch(() => ({}));

      if (!data?.success) {
        const msg = data?.message || "Email already in use.";
        setErr(msg);
        alert(msg);
        return;
      }

      // persist registration response
      localStorage.setItem("bv.registerResponse", JSON.stringify(data));
      alert(
        `Account created. Welcome to BizVantage! You selected the ${selectedPlan} plan.`,
      );
      navigate("/dashboard");
    } catch (e) {
      alert(e.message || "Failed to register.");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) {
      setErr("Email is required to reset password.");
      alert("Email is required to reset password.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(
        RESET_URL,
        { email: email.trim() },
        { headers: { "Content-Type": "application/json" } },
      );
      setForgotDialog(true);
      setForgotMode(false);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send password reset link.";
      setErr(msg);
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSignUpStep(1);
  };

  const TERMS_CONTENT = `
    <div>
      <p><strong>Last updated: June 18, 2026</strong></p>
      <p>These Terms &amp; Conditions govern your use of the GoWest.ai & BizVantage.ai website, services, BizVantage offerings, assessments, reports, policy documents, advisory services, workshops, subscriptions, digital deliverables, and related offerings provided by West McDonald Co. operating as GoWest.ai & BizVantage.ai ("GoWest.ai & BizVantage.ai," "we," "our," or "us").</p>
      <p>By using our website, purchasing services, subscribing to BizVantage, creating an account, engaging GoWest.ai & BizVantage.ai for advisory work, or using any GoWest.ai & BizVantage.ai or BizVantage deliverables, you agree to these Terms &amp; Conditions.</p>

      <br/><br/><h4>1. Who We Are</h4>
      <p>GoWest.ai & BizVantage.ai is operated by West McDonald Co. and provides business-to-business AI advisory, assessment, policy, governance, workflow, and enablement services.</p>
      <p>Our customers include businesses, MSPs, IT service providers, office equipment dealers, office products dealers, consultants, advisors, and channel partners.</p>

      <br/><br/><h4>2. Our Services</h4>
      <p>GoWest.ai & BizVantage.ai services may include, but are not limited to:</p>
      <ul>
        <li>AI readiness assessments</li>
        <li>BizVantage assessment and partner services</li>
        <li>AI workflow assessments</li>
        <li>AI policy and governance support</li>
        <li>Workflow opportunity analysis</li>
        <li>Executive summaries and strategic recommendations</li>
        <li>AI transformation planning</li>
        <li>Fractional CAIO advisory</li>
        <li>Workshops, training sessions, and enablement support</li>
        <li>Custom AI assistant and workflow recommendations</li>
        <li>Policies, procedures, reports, templates, and related digital deliverables</li>
      </ul>

      <br/><br/><h4>3. BizVantage Services</h4>
      <p>BizVantage is a business-to-business AI assessment, workflow, policy, and enablement offering designed for businesses, MSPs, dealers, consultants, advisors, and channel partners. BizVantage services may include AI workflow assessments, policies, procedures, reports, customer-facing recommendations, and related digital outputs. BizVantage services are typically billed quarterly, upfront, unless otherwise stated in a written agreement.</p>

      <br/><br/><h4>4. BizVantage Partner Use and White-Label Rights</h4>
      <p>Approved BizVantage partners may use BizVantage assessment outputs, AI workflow assessments, policies, procedures, and related deliverables with their own customers, subject to these Terms and any applicable partner agreement. White-label rights apply only to approved BizVantage outputs and do not transfer ownership of GoWest.ai & BizVantage.ai's underlying methods, systems, templates, software, processes, platform, know-how, trademarks, branding, or intellectual property unless explicitly stated in a separate written agreement.</p>

      <br/><br/><h4>5. Business Advisory Only</h4>
      <p>GoWest.ai & BizVantage.ai provides business advisory, workflow, AI readiness, governance, policy, and implementation guidance. GoWest.ai & BizVantage.ai does not provide legal, medical, financial, tax, insurance, employment, credit, investment, accounting, audit, or regulated professional advice unless explicitly stated in a separate written agreement and performed by appropriately qualified professionals.</p>

      <br/><br/><h4>6. No Guaranteed Results</h4>
      <p>GoWest.ai & BizVantage.ai works to identify practical, high-value AI opportunities and support responsible AI adoption. However, GoWest.ai & BizVantage.ai does not guarantee specific revenue, profit, savings, productivity gains, efficiency improvements, customer acquisition, customer retention, implementation outcomes, regulatory outcomes, or business results.</p>

      <br/><br/><h4>7. Client and Partner Responsibilities</h4>
      <p>Clients and partners are responsible for providing accurate, complete, and timely information required to perform the services and for reviewing all deliverables before use.</p>

      <br/><br/><h4>8. Sensitive Data Restrictions</h4>
      <p>Clients, partners, and users should not upload or submit unnecessary sensitive information unless specifically requested and appropriate safeguards agreed.</p>

      <br/><br/><h4>9. Payments and Billing</h4>
      <p>Fees are due according to the payment terms shown on the applicable invoice, checkout page, proposal, subscription plan, order form, statement of work, or written agreement. BizVantage subscriptions are generally billed quarterly, upfront, unless otherwise stated in writing.</p>

      <br/><br/><h4>10. Refunds</h4>
      <p>All payments are non-refundable once payment has been made, except in cases of confirmed billing error, duplicate charge, or incorrect charge. Contact lesley@gowest.ai before initiating a dispute.</p>

      <br/><br/><h4>11. Cancellations</h4>
      <p>Cancellation requests must be submitted to lesley@gowest.ai. Cancellation takes effect at the end of the current paid billing period unless otherwise stated.</p>

      <br/><br/><h4>12. Scope Changes</h4>
      <p>Any work outside the agreed scope may require a separate proposal, change order, or additional fees.</p>

      <br/><br/><h4>13. Third-Party Tools and Platforms</h4>
      <p>Some services may involve third-party AI tools, payment processors, hosting providers, or integrations. GoWest.ai & BizVantage.ai is not responsible for third-party availability or practices.</p>

      <br/><br/><h4>14. AI Outputs and Human Review</h4>
      <p>AI-assisted outputs may contain errors. Review all AI outputs with qualified humans before relying on them.</p>

      <br/><br/><h4>15. Confidentiality</h4>
      <p>Both parties agree to use reasonable care to protect confidential information and not disclose it except as required to provide services or comply with law.</p>

      <br/><br/><h4>16. Intellectual Property</h4>
      <p>GoWest.ai & BizVantage.ai retains ownership of pre-existing materials. Upon full payment, clients receive a limited right to use final deliverables for internal business purposes.</p>

      <br/><br/><h4>17. Website and Service Use</h4>
      <p>You agree not to misuse the website or services or attempt unauthorized access, scraping, or other harmful activities.</p>

      <br/><br/><h4>18. Suspension or Termination</h4>
      <p>GoWest.ai & BizVantage.ai may suspend or terminate access for violations, non-payment, misuse, or other risks as described in these Terms.</p>

      <br/><br/><h4>19. Limitation of Liability</h4>
      <p>To the maximum extent permitted by law, GoWest.ai & BizVantage.ai will not be liable for indirect or consequential damages. GoWest.ai & BizVantage.ai's total liability will not exceed amounts paid for the service during the three months before the claim.</p>

      <br/><br/><h4>20. Indemnification</h4>
      <p>You agree to indemnify GoWest.ai & BizVantage.ai for claims arising from your misuse of services or violation of these Terms.</p>

      <br/><br/><h4>21. Governing Law</h4>
      <p>These Terms are governed by the laws of the Province of Ontario and the applicable laws of Canada.</p>

      <br/><br/><h4>22. Changes to These Terms</h4>
      <p>GoWest.ai & BizVantage.ai may update these Terms. Continued use after updates constitutes acceptance.</p>

      <br/><br/><h4>23. Contact</h4>
      <p>Questions: West McDonald Co. operating as GoWest.ai & BizVantage.ai<br/>Email: lesley@gowest.ai<br/>Phone: +1 647-992-6775</p>
    </div>
  `;

  const PRIVACY_CONTENT = `
    <div>
      <p>GoWest.ai & BizVantage.ai is operated by West McDonald Co. operating as GoWest.ai & BizVantage.ai ("GoWest.ai & BizVantage.ai," "we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, store, and protect information when you visit our website, contact us, create an account, purchase services, subscribe to BizVantage, participate in assessments, engage us for consulting or advisory services, or otherwise interact with GoWest.ai & BizVantage.ai.</p>

      <p>By using our website, services, BizVantage offerings, forms, subscriptions, assessments, or related deliverables, you agree to the practices described in this Privacy Policy.</p>

      <br/><br/><h4>1. Who We Serve</h4>
      <p>GoWest.ai & BizVantage.ai provides business-to-business AI advisory, assessment, policy, governance, workflow, and enablement services. Our customers may include businesses, MSPs, IT service providers, office equipment dealers, office products dealers, consultants, advisors, and channel partners.</p>

      <br/><br/><h4>2. Information We May Collect</h4>
      <p>Depending on how you interact with GoWest.ai & BizVantage.ai, we may collect name, company name, job title or role, business email address, business phone number, billing information, account registration information, subscription information, contact form submissions, consultation requests, assessment responses, workflow descriptions, business process information, meeting notes or transcripts, project documents, policy or procedure documents, technology stack information, customer-facing assessment information, website usage information, communication preferences, and any other information you choose to provide.</p>

      <br/><br/><h4>3. Payment Information</h4>
      <p>Payments may be processed by Stripe or another third-party payment processor. GoWest.ai & BizVantage.ai does not store full credit card numbers on its own servers. Payment information is handled by the applicable payment processor. For billing questions, contact lesley@gowest.ai.</p>


      {/* Payment & Billing Dialog */}
      <Dialog open={billingOpen} onOpenChange={setBillingOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Payment & Billing Terms</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground max-h-[60vh] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: PAYMENT_CONTENT }} />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setBillingOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <br/><br/><h4>4. BizVantage and Assessment Information</h4>
      <p>If you use BizVantage or related assessment services, we may collect and process information needed to provide assessment, policy, workflow, reporting, or partner services.</p>

      <br/><br/><h4>5. Sensitive Data Restrictions</h4>
      <p>Clients, partners, and users should not upload, submit, email, or otherwise provide unnecessary sensitive information to GoWest.ai & BizVantage.ai unless specifically requested and appropriate safeguards have been agreed.</p>

      <br/><br/><h4>6. How We Use Information</h4>
      <p>GoWest.ai & BizVantage.ai may use information to respond to inquiries, provide services, manage subscriptions, process payments, prepare deliverables, provide support, improve our website and services, maintain security, manage partnerships, comply with legal obligations, and send marketing communications where permitted.</p>

      <br/><br/><h4>7. Marketing Communications</h4>
      <p>GoWest.ai & BizVantage.ai may send marketing communications where permitted. You may unsubscribe using the instructions in the message or contact lesley@gowest.ai.</p>

      <br/><br/><h4>8. Cookies and Website Analytics</h4>
      <p>Our website may use cookies and analytics tools to understand website usage and improve user experience. You may adjust your browser settings to block or delete cookies.</p>

      <br/><br/><h4>9. Third-Party Tools and Service Providers</h4>
      <p>We may use third-party tools and service providers to help operate the business and deliver services. These providers may process or store information on our behalf.</p>

      <br/><br/><h4>10. AI-Assisted Workflows</h4>
      <p>GoWest.ai & BizVantage.ai may use AI-assisted tools to support analysis and drafting. AI-assisted outputs may contain errors and should be reviewed by qualified humans.</p>

      <br/><br/><h4>11. How We Share Information</h4>
      <p>GoWest.ai & BizVantage.ai does not sell personal information. We may share information with trusted service providers, partners, payment processors, professional advisors, or as required by law.</p>

      <br/><br/><h4>12. Client and Partner Responsibilities</h4>
      <p>Clients and partners are responsible for ensuring they have the necessary rights and consents to provide information to GoWest.ai & BizVantage.ai.</p>

      <br/><br/><h4>13. Data Retention</h4>
      <p>We retain information as long as reasonably necessary to provide services, manage accounts, comply with legal obligations, and operate the business.</p>

      <br/><br/><h4>14. Data Security</h4>
      <p>GoWest.ai & BizVantage.ai uses reasonable safeguards to protect information but cannot guarantee absolute security.</p>

      <br/><br/><h4>15. International Processing</h4>
      <p>Information may be processed or stored outside your province, state, or country.</p>

      <br/><br/><h4>16. Your Rights and Choices</h4>
      <p>You may have rights to access, correct, update, delete, or request information about personal information we maintain. Contact lesley@gowest.ai to make a request.</p>

      <br/><br/><h4>17. Children's Privacy</h4>
      <p>We do not knowingly collect personal information from children.</p>

      <br/><br/><h4>18. Links to Third-Party Websites</h4>
      <p>We are not responsible for the privacy practices of third-party websites or services.</p>

      <br/><br/><h4>19. Changes to This Privacy Policy</h4>
      <p>We may update this Privacy Policy from time to time. Continued use after updates constitutes acceptance.</p>

      <br/><br/><h4>20. Contact</h4>
      <p>Questions: West McDonald Co. operating as GoWest.ai & BizVantage.ai<br/>Email: lesley@gowest.ai<br/>Phone: +1 647-992-6775</p>
    </div>
  `;

  const REFUND_CONTENT = `
    <div>
      <p>GoWest.ai & BizVantage.ai is operated by West McDonald Co. and provides business-to-business AI advisory, assessment, policy, governance, workflow, and enablement services.</p>

      <p>This Refund Policy applies to payments made for GoWest.ai & BizVantage.ai and BizVantage services unless a separate written agreement states otherwise.</p>

      <br/><br/><h4>General Refund Policy</h4>
      <p>All payments are non-refundable once payment has been made, except in cases of confirmed billing error, duplicate charge, or incorrect charge.</p>
      <p>Because GoWest.ai & BizVantage.ai provides professional services, advisory work, assessment access, reserved capacity, digital deliverables, business analysis, and subscription-based services, payments are not refundable after purchase.</p>

      <br/><br/><h4>BizVantage Subscriptions</h4>
      <p>BizVantage services are billed quarterly, upfront. Once a quarterly BizVantage subscription payment has been processed, the payment is non-refundable except in cases of confirmed billing error, duplicate charge, or incorrect charge. Cancellation of a BizVantage subscription stops future billing but does not create a refund for the current paid billing period.</p>

      <br/><br/><h4>Overage Payments</h4>
      <p>If a customer or partner uses more than the included amount during the subscription period, GoWest.ai & BizVantage.ai may charge one-time overage fees. Overage fees are non-refundable once payment has been made, except in cases of confirmed billing error, duplicate charge, or incorrect charge.</p>

      <br/><br/><h4>Consulting, Advisory, and Project Services</h4>
      <p>Unless a separate written agreement states otherwise, payments for consulting, advisory, workshops, training, or project services are non-refundable once payment has been made, except in cases of confirmed billing error, duplicate charge, or incorrect charge.</p>

      <br/><br/><h4>Billing Errors</h4>
      <p>If you believe you were charged incorrectly, charged twice, or billed the wrong amount, contact us at lesley@gowest.ai. Include your name, company name, billing email, invoice number if available, description of the billing issue, and date and amount of the charge.</p>

      <br/><br/><h4>Payment Disputes</h4>
      <p>Please contact GoWest.ai & BizVantage.ai before initiating a chargeback or payment dispute. Billing, refund, and payment dispute contact: lesley@gowest.ai Phone: +1 647-992-6775. We aim to respond within 2 business days.</p>
    </div>
  `;

  const CANCELLATION_CONTENT = `
    <div>
      <p>GoWest.ai & BizVantage.ai is operated by West McDonald Co. and provides business-to-business AI advisory, assessment, policy, governance, workflow, and enablement services.</p>

      <p>This Cancellation Policy applies to GoWest.ai & BizVantage.ai and BizVantage services unless a separate written agreement states otherwise.</p>

      <br/><br/><h4>How to Cancel</h4>
      <p>Cancellation requests must be submitted directly to GoWest.ai & BizVantage.ai by email. Send cancellation requests to lesley@gowest.ai.</p>
      <p>Please include your name, company name, billing email, service or subscription name, requested cancellation date, and any relevant invoice or account information. GoWest.ai & BizVantage.ai aims to respond to cancellation requests within 2 business days or sooner.</p>

      <br/><br/><h4>BizVantage Quarterly Subscriptions</h4>
      <p>BizVantage services are billed quarterly, upfront. When a BizVantage subscription is cancelled, cancellation takes effect at the end of the current paid billing period. Access remains until the end of the paid period unless otherwise stated. Cancellation prevents future billing but does not create a refund for the current paid billing period.</p>

      <br/><br/><h4>Timing of Cancellation Requests</h4>
      <p>To avoid renewal charges, cancellation requests should be submitted before the next scheduled billing date. If a cancellation request is submitted after a quarterly payment has already been processed, that payment is non-refundable except in cases of confirmed billing error, duplicate charge, or incorrect charge.</p>

      <br/><br/><h4>Larger Consulting Agreements</h4>
      <p>Larger consulting engagements, custom projects, or retainers may be governed by a separate written agreement; cancellation terms in those agreements apply.</p>

      <br/><br/><h4>Access After Cancellation</h4>
      <p>Access generally remains available until the end of the current paid billing period. GoWest.ai & BizVantage.ai may remove access earlier if required by law, contract, security concern, misuse, non-payment, or violation of applicable terms.</p>

      <br/><br/><h4>Contact</h4>
      <p>For cancellation requests, billing questions, refund questions, or payment disputes, email lesley@gowest.ai or call +1 647-992-6775. We aim to respond within 2 business days or sooner.</p>
    </div>
  `;

  const PAYMENT_CONTENT = `
    <div>
      <p><strong>Last updated: June 18, 2026</strong></p>
      <p>GoWest.ai & BizVantage.ai is operated by West McDonald Co. operating as GoWest.ai & BizVantage.ai. These Payment & Billing Terms explain how payments, billing, subscriptions, overages, and billing support are handled for GoWest.ai & BizVantage.ai and BizVantage services. These terms apply unless a separate written agreement states otherwise.</p>

      <br/><br/><h4>1. Payment Methods</h4>
      <p>GoWest.ai & BizVantage.ai may accept payment by credit card, debit card, ACH, wire transfer, electronic funds transfer, or other approved payment methods. Online payments may be processed securely through Stripe or another third-party payment processor. GoWest.ai & BizVantage.ai does not store full credit card numbers on its own servers.</p>

      <br/><br/><h4>2. BizVantage Billing</h4>
      <p>BizVantage services are billed quarterly, upfront, unless otherwise stated in a written agreement. By purchasing or subscribing to BizVantage, you authorize GoWest.ai & BizVantage.ai and its payment processor to charge the selected payment method according to the applicable quarterly billing schedule.</p>

      <br/><br/><h4>3. Overage Payments</h4>
      <p>Some BizVantage subscriptions, partner plans, or service packages may include a set number of assessments, AI workflow assessments, policy outputs, procedure outputs, customer-facing deliverables, usage credits, or other included services during a billing period.</p>
      <p>If a customer or partner uses more than the included amount during the subscription period, GoWest.ai & BizVantage.ai may charge one-time overage fees. Overage fees may be charged by invoice, payment link, saved payment method, Stripe, or another approved payment method. Overage fees are non-refundable once payment has been made, except in cases of confirmed billing error, duplicate charge, or incorrect charge. GoWest.ai & BizVantage.ai will make reasonable efforts to communicate applicable overage fees, usage limits, or additional charges before processing or requesting payment.</p>

      <br/><br/><h4>4. Access After Payment</h4>
      <p>For BizVantage subscriptions, access is generally provisioned immediately after successful payment. Some services, deliverables, reports, assessments, policy outputs, partner setup, onboarding activities, or customer-facing outputs may require additional information, account setup, onboarding, customer submissions, workflow inputs, or other client or partner participation before they can be completed. Immediate access after payment does not guarantee immediate completion of all assessments, reports, policies, procedures, consulting services, or other deliverables.</p>

      <br/><br/><h4>5. GoWest.ai & BizVantage.ai Consulting and Advisory Services</h4>
      <p>GoWest.ai & BizVantage.ai services outside of BizVantage may include consulting, advisory services, AI readiness assessments, policy development, implementation planning, workshops, training, Fractional CAIO support, or related professional services. Payment terms for these services may be shown on the applicable invoice, proposal, order form, checkout page, statement of work, or written agreement. GoWest.ai & BizVantage.ai services outside of BizVantage are not necessarily billed through Stripe and may be paid through another approved payment method.</p>

      <br/><br/><h4>6. Subscriptions and Recurring Billing</h4>
      <p>For subscription or recurring services, billing occurs according to the applicable subscription plan, checkout page, invoice, order form, or written agreement. By purchasing a subscription or recurring service, you authorize GoWest.ai & BizVantage.ai and its payment processor to charge the payment method on file according to the applicable billing schedule. For BizVantage quarterly subscriptions, charges are processed upfront for the upcoming quarterly billing period.</p>

      <br/><br/><h4>7. Failed Payments</h4>
      <p>If a payment fails, GoWest.ai & BizVantage.ai may retry the payment, request updated payment information, pause access, suspend services, delay deliverables, or terminate access until payment is received.</p>

      <br/><br/><h4>8. Taxes</h4>
      <p>Prices may not include applicable taxes unless stated otherwise. Customers are responsible for applicable sales, use, value-added, goods and services, harmonized sales, or similar taxes where required by law.</p>

      <br/><br/><h4>9. No Refunds Except Billing Errors</h4>
      <p>All payments are non-refundable once payment has been made, except in cases of confirmed billing error, duplicate charge, or incorrect charge. Please review GoWest.ai & BizVantage.ai's Refund Policy for more details.</p>

      <br/><br/><h4>10. Cancellation and Future Billing</h4>
      <p>Cancellation requests must be sent directly to GoWest.ai & BizVantage.ai at lesley@gowest.ai. For BizVantage and other subscription-based services, cancellation takes effect at the end of the current paid billing period unless otherwise stated in a written agreement. Cancellation prevents future billing but does not create a refund for the current paid billing period.</p>

      <br/><br/><h4>11. Billing Questions and Payment Disputes</h4>
      <p>For billing questions, duplicate charges, incorrect charges, subscription questions, invoice questions, cancellation requests, or payment disputes, contact lesley@gowest.ai. Please contact GoWest.ai & BizVantage.ai before initiating a chargeback or payment dispute. GoWest.ai & BizVantage.ai aims to respond within 2 business days or sooner.</p>

      <br/><br/><h4>12. Changes to Billing Terms</h4>
      <p>GoWest.ai & BizVantage.ai may update these Payment & Billing Terms from time to time. Updates will be posted on this page with a revised "Last updated" date.</p>
    </div>
  `;

  const openTermsDialog = () => {
    setTermsHtml(TERMS_CONTENT);
    setTermsOpen(true);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bg})` }}
    >
      {loading && <FullScreenLoader />}
      <Card
        className={`w-full shadow-xl border border-white/10 bg-white/[0.02] backdrop-blur-md transition-all ${signUpStep === 2 && activeTab === "signup" ? "max-w-2xl" : "max-w-md"}`}
      >
        <CardContent className="pt-8 pb-8 px-8 text-white">
          <div className="flex flex-col items-center mb-8">
            <img
              src={logo}
              alt="BizVantage"
              className="h-[83px] w-auto object-contain mb-2 brightness-0 invert"
            />
            <p className="text-sm italic text-white/70">
              Aim higher with the help of AI
            </p>
          </div>
          {forgotMode ? (
            <form onSubmit={forgotPassword} className="space-y-4">
              <p className="text-sm text-white/70 text-center">
                Enter your email and we'll send you a reset link.
              </p>
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-white">
                  Email
                </Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={255}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
              </div>
              <Button
                type="submit"
                onClick={forgotPassword}
                className="w-full text-white"
                disabled={!email.trim()}
              >
                Submit
              </Button>
              <button
                type="button"
                onClick={() => setForgotMode(false)}
                className="w-full text-sm text-white/80 hover:underline"
              >
                Back to Sign In
              </button>
            </form>
          ) : (
            <>
              <div className="flex mb-6 border-b border-white/20">
                <button
                  type="button"
                  onClick={() => handleTabSwitch("signin")}
                  className={`flex-1 pb-2 text-sm font-medium transition-colors ${
                    activeTab === "signin"
                      ? "text-white border-b-2 border-white"
                      : "text-white/50 hover:text-white/70"
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => handleTabSwitch("signup")}
                  className={`flex-1 pb-2 text-sm font-medium transition-colors ${
                    activeTab === "signup"
                      ? "text-white border-b-2 border-white"
                      : "text-white/50 hover:text-white/70"
                  }`}
                >
                  Sign Up
                </button>
              </div>

              {activeTab === "signin" ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      maxLength={255}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-white">
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => setForgotMode(true)}
                        className="text-xs text-white hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      maxLength={128}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white"
                    disabled={!email.trim() || !password.trim() || loading}
                  >
                    {loading ? "Logging in..." : "Sign In"}
                  </Button>
                </form>
              ) : signUpStep === 1 ? (
                <form onSubmit={handleSignUpStep1} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name" className="text-white">
                      Full Name
                    </Label>
                    <Input
                      id="signup-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      maxLength={100}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-title" className="text-white">
                      Title / Role
                    </Label>
                    <Input
                      id="signup-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. CEO, VP Engineering"
                      maxLength={100}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-white">
                      Email
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      maxLength={255}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-white">
                      Password
                    </Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      maxLength={128}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm" className="text-white">
                      Confirm Password
                    </Label>
                    <Input
                      id="signup-confirm"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      maxLength={128}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-currency" className="text-white">
                      Country
                    </Label>
                    <select
                      id="signup-currency"
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      className="w-full rounded-md border border-white/20 bg-white/10 px-3 py-2 text-white placeholder:text-white/50"
                    >
                      <option value="USD">United States</option>
                      <option value="CAD">Canada</option>
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white"
                    disabled={
                      !name.trim() ||
                      !email.trim() ||
                      !password.trim() ||
                      !confirmPassword.trim()
                    }
                  >
                    Continue — Select Plan
                  </Button>
                </form>
              ) : (
                <DiscountProvider>
                  <SignUpPlanStep
                    selectedPlan={selectedPlan}
                    onSelectPlan={setSelectedPlan}
                    onBack={() => setSignUpStep(1)}
                    onComplete={handleSignUpComplete}
                  />
                </DiscountProvider>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <div className="mt-8 flex flex-col items-center gap-3">
        <p className="text-sm font-bold text-white">
          Curious to learn what BizVantage does?
        </p>
        <Button
          variant="outline"
          className="border-white/30 text-white bg-white/10 hover:bg-white/20"
          onClick={() =>
            window.open("https://gowest.ai/services/bizvantage/", "_blank")
          }
        >
          Learn More
        </Button>
      </div>

      <Dialog open={forgotDialog} onOpenChange={setForgotDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Password Reset</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Password has been sent to your registered email.
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setForgotDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={subscriptionCancelledDialog}
        onOpenChange={setSubscriptionCancelledDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              Subscription Cancelled
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Your subscription process was cancelled. If you want to try again,
            please select a plan and continue.
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setSubscriptionCancelledDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={subscriptionSuccessDialog}
        onOpenChange={setSubscriptionSuccessDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              Subscription Success
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Your subscription process was successful. You can now continue using
            BizVantage with the registred email and password. Welcome aboard!
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setSubscriptionSuccessDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terms & Conditions Dialog */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Terms & Conditions
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground max-h-[60vh] overflow-auto">
            {termsLoading ? (
              <p>Loading...</p>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setTermsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground max-h-[60vh] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: PRIVACY_CONTENT }} />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setPrivacyOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Policy Dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Refund Policy</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground max-h-[60vh] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: REFUND_CONTENT }} />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setRefundOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Policy Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              Cancellation Policy
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground max-h-[60vh] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: CANCELLATION_CONTENT }} />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button onClick={() => setCancelOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer with Terms link */}
      <footer className="mt-8 w-full text-center text-white/80">
        <div className="max-w-5xl mx-auto py-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => openTermsDialog()}
            className="text-sm underline"
          >
            Terms & Conditions
          </button>
          <button
            type="button"
            onClick={() => setPrivacyOpen(true)}
            className="text-sm underline"
          >
            Privacy Policy
          </button>
          <button
            type="button"
            onClick={() => setRefundOpen(true)}
            className="text-sm underline"
          >
            Refund Policy
          </button>
          <button
            type="button"
            onClick={() => setBillingOpen(true)}
            className="text-sm underline"
          >
            Payment & Billing Terms
          </button>
          <button
            type="button"
            onClick={() => setCancelOpen(true)}
            className="text-sm underline"
          >
            Cancellation Policy
          </button>
        </div>
      </footer>
    </div>
  );
};

export default LoginNewV1;
